import type { ReactNode } from 'react';

import { cn } from './utils';

/** The identity kinds an Overview count is split by: human, non-human, external. */
export type IdentityKind = 'HI' | 'NHI' | 'External';

const KIND_NOUN = {
  HI: 'human identity',
  NHI: 'non-human identity',
  External: 'external identity',
} as const satisfies Record<IdentityKind, string>;

/** "Show non-human identity events for Q4 Board Deck"; no subject for a card total. */
export function userTypeDrillLabel(kind: IdentityKind, subject?: string): string {
  return `Show ${KIND_NOUN[kind]} events${subject ? ` for ${subject}` : ''}`;
}

/**
 * Classes for a clickable row's primary-cell `<button>` (the file name or the
 * actor). The row itself carries `group/drill` and the mouse click; this button
 * is the keyboard path, and its activation bubbles to the row's handler. The
 * ring is inset because the cell clips overflow.
 */
export const DRILL_PRIMARY_BUTTON_CLASS =
  'min-w-0 rounded-[2px] text-left outline-none group-hover/drill:underline focus-visible:underline focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-dawn-orange';

export interface UserTypeDrillButtonProps {
  kind: IdentityKind;
  /** What the count belongs to (a file, a tier, a data row); omit for a card total. */
  subject?: string;
  onClick: () => void;
  className?: string;
  children: ReactNode;
}

/**
 * An identity count that drills into the events grid for that one kind. It is
 * its own button, never nested in a row button, and it stops the click so a
 * clickable row around it does not fire as well.
 */
export function UserTypeDrillButton({
  kind,
  subject,
  onClick,
  className,
  children,
}: UserTypeDrillButtonProps) {
  return (
    <button
      type="button"
      aria-label={userTypeDrillLabel(kind, subject)}
      onClick={(event) => {
        event.stopPropagation();
        onClick();
      }}
      className={cn(
        'inline-flex cursor-pointer items-center rounded-[2px] outline-none hover:bg-grey-100 focus-visible:ring-2 focus-visible:ring-dawn-orange',
        className
      )}
    >
      {children}
    </button>
  );
}
